import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {LocationPdfType, PdfOrientation} from "./pdfConstants";

/**
 * State für die rote PDF-Karten-Vorschau-Box auf der interaktiven Karte.
 * Wird beim Mouse-Over eines PDF-Export-Buttons gesetzt und blendet auf der
 * Karte nach kurzer Zeit wieder aus.
 *
 * - {@link PdfPreviewState.type} identifiziert den Karten-Typ (z.B. "overview",
 *   "micro-location", "traffic" …) und entspricht der `type`-Prop des zugehörigen
 *   Export-Buttons.
 * - {@link PdfPreviewState.orientation} ist die Ausrichtung ("horizontal"/"vertical")
 *   oder `null`, falls der Export keine Ausrichtung kennt (Overview, Traffic, Nearby).
 * - {@link PdfPreviewState.nonce} wird bei jedem Hover erhöht und dient als
 *   Re-Trigger, damit auch wiederholtes Überfahren desselben Buttons die Box
 *   erneut einblendet.
 */
export interface PdfPreviewState {
    type: LocationPdfType | null;
    orientation: PdfOrientation | null;
    nonce: number;
    /** true, während für type/orientation gerade ein PDF generiert wird (Box „atmet"). */
    generating: boolean;
}

const initialState: PdfPreviewState = {
    type: null,
    orientation: null,
    nonce: 0,
    generating: false,
};

export const pdfPreviewSlice = createSlice({
    name: "pdfPreview",
    initialState,
    reducers: {
        /**
         * Zeigt die Vorschau-Box für den gegebenen Karten-Typ/-Orientierung an.
         * `nonce` wird immer erhöht (auch erneutes Hovern desselben Buttons ist ein
         * neuer Trigger); `generating` steuert den „atmenden" Zustand während der
         * PDF-Generierung (Hover = false, Generierungsbeginn = true, -ende = false).
         */
        showPdfPreview: (
            state,
            action: PayloadAction<{type: LocationPdfType; orientation?: PdfOrientation | null; generating?: boolean}>,
        ) => {
            state.type = action.payload.type;
            state.orientation = action.payload.orientation ?? null;
            state.nonce = state.nonce + 1;
            state.generating = action.payload.generating ?? false;
        },
        /**
         * Blendet die Vorschau-Box aus (nonce = 0 -> {@link usePdfPreview} liefert null).
         * Genutzt z.B. beim Umschalten des Karten-Themas im Infrastruktur-Tab, damit eine
         * offene Box nicht auf der neuen Karte hängen bleibt.
         */
        hidePdfPreview: (state) => {
            state.type = null;
            state.orientation = null;
            state.nonce = 0;
            state.generating = false;
        },
    },
    selectors: {
        selectPdfPreview: (state) => state,
    },
});

export const {showPdfPreview, hidePdfPreview} = pdfPreviewSlice.actions;

export const {selectPdfPreview} = pdfPreviewSlice.selectors;

export default pdfPreviewSlice.reducer;
